import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Receipt, CaretRight, CalendarCheck, Lightning, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AUTOPAY_STATUS_META,
  centsToUsd,
  describeAutopayRetry,
  disableAutopay,
  invoiceStatusLabel,
  listInvoicesForProfile,
  openParentInvoicePdf,
  startAutopaySetup,
  startCheckoutSession,
} from "@/lib/billing";
import AutopayStatusChip from "@/components/billing/AutopayStatusChip";
import { getTuitionPlanForProfile } from "@/lib/tuitionPlans";
import { getParentSubsidyInvoiceSummaries } from "@/lib/subsidyBilling";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

export default function ParentBilling() {
  const { profile } = useAuth();
  const [profileRow, setProfileRow] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [plan, setPlan] = useState(null);
  const [autopayBusy, setAutopayBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    if (!profile?.id || !isSupabaseConfigured) return;
    setLoading(true);
    setLoadError("");
    try {
      // RLS on billing_profiles already restricts to families the parent
      // belongs to (via family_members). Pick the most recent one — the
      // Phase 2 assumption is one active billing family per parent.
      const { data: profiles, error } = await supabase
        .from("billing_profiles")
        .select(
          "id, family_id, billing_email, stripe_customer_id, status, autopay, autopay_status, autopay_failure_reason, stripe_payment_method_id, autopay_enrolled_at",
        )
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const bp = (profiles || [])[0] || null;
      setProfileRow(bp);
      if (bp?.id) {
        const [list, tp, subsidySummaries] = await Promise.all([
          listInvoicesForProfile(bp.id, { parentView: true }),
          getTuitionPlanForProfile(bp.id),
          getParentSubsidyInvoiceSummaries(),
        ]);
        const summariesByInvoice = new Map(
          subsidySummaries.map((summary) => [summary.invoice_id, summary]),
        );
        setInvoices(list.map((invoice) => ({
          ...invoice,
          subsidy: summariesByInvoice.get(invoice.id) || null,
        })));
        setPlan(tp);
      } else {
        setInvoices([]);
      }
    } catch (err) {
      const message = err.message || "Could not load billing";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const openInvoices = invoices.filter((i) => i.status === "issued" || i.status === "failed");
  const historyInvoices = invoices.filter((i) => i.status === "paid" || i.status === "void");
  const openBalance = openInvoices.reduce(
    (acc, invoice) => acc + (invoice.subsidy?.has_agency_coverage
      ? Number(invoice.subsidy.family_open_cents || 0)
      : Number(invoice.amount_total_cents || 0)),
    0,
  );
  const coveredInvoiceCount = openInvoices.filter((invoice) => invoice.subsidy?.has_agency_coverage).length;

  const onPay = async (invoice) => {
    setPaying(invoice.id);
    try {
      const { url } = await startCheckoutSession(invoice.id);
      if (!url || !/^https?:\/\//.test(url)) {
        throw new Error("Checkout did not return a payment link. Please try again later.");
      }
      window.location.assign(url);
    } catch (err) {
      toast.error(err.message || "Payment setup failed");
    } finally {
      setPaying(null);
    }
  };

  const onStartAutopay = async () => {
    if (!profileRow?.id) return;
    setAutopayBusy(true);
    try {
      const { url } = await startAutopaySetup(profileRow.id);
      if (!url || !/^https?:\/\//.test(url)) {
        throw new Error("Autopay setup did not return a link. Please try again later.");
      }
      window.location.assign(url);
    } catch (err) {
      toast.error(err.message || "Could not start autopay setup");
      setAutopayBusy(false);
    }
  };

  const onDisableAutopay = async () => {
    if (!profileRow?.id) return;
    if (!window.confirm("Turn off automatic payments? You'll need to pay future invoices manually.")) return;
    setAutopayBusy(true);
    try {
      await disableAutopay(profileRow.id);
      toast.success("Autopay turned off");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not disable autopay");
    } finally {
      setAutopayBusy(false);
    }
  };

  return (
    <div data-testid="parent-billing" className="space-y-5">
      <header>
        <p className="overline text-primary-hover">Billing</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Your account
        </h1>
        <Link
          to="/parent/billing/history"
          data-testid="parent-billing-history-link"
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-hover hover:underline"
        >
          Payment history <CaretRight size={10} weight="bold" />
        </Link>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : loadError ? (
        <div
          data-testid="parent-billing-error"
          role="alert"
          className="rounded-2xl border border-[#C6851E]/40 bg-[#C6851E]/10 p-5"
        >
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="duotone" className="mt-0.5 text-[#8A5A12]" />
            <div>
              <p className="font-heading font-semibold text-neutral-900">Your account is temporarily unavailable</p>
              <p className="mt-1 text-sm text-neutral-700">We could not safely load your current responsibility. No payment action is available until the account refreshes.</p>
              <button type="button" onClick={load} className="mt-3 min-h-9 rounded-full border border-primary/40 bg-white px-3 text-sm font-semibold text-primary-hover hover:bg-primary/5">Try again</button>
            </div>
          </div>
        </div>
      ) : !profileRow ? (
        <div
          data-testid="parent-billing-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            No billing account yet
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Once your enrollment is approved and finalised, a billing
            profile will be created automatically.
          </p>
        </div>
      ) : (
        <>
          <section
            data-testid="parent-billing-summary"
            className={`rounded-2xl border p-5 shadow-card ${
              openBalance > 0 ? "border-warning/40 bg-warning-soft" : "border-success/30 bg-success-soft"
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white shadow-card">
                <CreditCard size={22} weight="duotone" className="text-primary-hover" />
              </span>
              <div>
                <p className="overline text-neutral-500">Current balance</p>
                <p className="tnum mt-1 font-heading text-3xl font-bold tracking-tight text-neutral-900">
                  {centsToUsd(openBalance)}
                </p>
                <p className="mt-1 text-xs text-neutral-600">
                  {openInvoices.length === 0
                    ? "You're all caught up."
                    : `${openInvoices.length} open invoice${openInvoices.length === 1 ? "" : "s"}`}
                </p>
              </div>
            </div>
          </section>

          {plan && plan.is_active && (
            <section
              data-testid="parent-billing-plan"
              className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
            >
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <CalendarCheck size={18} weight="duotone" className="text-primary-hover" />
              </span>
              <div>
                <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
                  Monthly billing on day {plan.day_of_month}
                </p>
                <p className="mt-0.5 text-xs text-neutral-500">
                  {plan.pause_until && new Date(plan.pause_until) > new Date()
                    ? `Paused until ${new Date(plan.pause_until).toLocaleDateString()}.`
                    : `Next invoice scheduled for ${new Date(plan.next_run_on).toLocaleDateString()}.`}
                </p>
              </div>
            </section>
          )}

          <AutopayCard
            profile={profileRow}
            busy={autopayBusy}
            onSetup={onStartAutopay}
            onDisable={onDisableAutopay}
            openInvoices={openInvoices}
            coveredInvoiceCount={coveredInvoiceCount}
          />

          {openInvoices.length > 0 && (
            <section>
              <p className="overline mb-3 text-neutral-500">
                Open invoices
              </p>
              <ul className="space-y-2" data-testid="parent-open-invoices">
                {openInvoices.map((inv) => (
                  <li key={inv.id}>
                    <InvoiceCard
                      invoice={inv}
                      onPay={() => onPay(inv)}
                      paying={paying === inv.id}
                      showAutopayNudge={
                        Boolean(inv.due_date) &&
                        (profileRow?.autopay_status === "none" ||
                          profileRow?.autopay_status === "setup_failed")
                      }
                      onSetupAutopay={onStartAutopay}
                      autopayBusy={autopayBusy}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {historyInvoices.length > 0 && (
            <section>
              <p className="overline mb-3 text-neutral-500">
                History
              </p>
              <ul className="space-y-2" data-testid="parent-invoice-history">
                {historyInvoices.map((inv) => (
                  <li key={inv.id}>
                    <InvoiceCard invoice={inv} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function InvoiceCard({ invoice, onPay, paying, showAutopayNudge, onSetupAutopay, autopayBusy }) {
  const unpaid = invoice.status === "issued" || invoice.status === "failed";
  const hasAgencyCoverage = Boolean(invoice.subsidy?.has_agency_coverage);
  const familyOpen = Number(invoice.subsidy?.family_open_cents || 0);
  const retry = describeAutopayRetry(invoice);
  return (
    <div
      data-testid={`parent-invoice-${invoice.id}`}
      data-status={invoice.status}
      data-autopay-retry={retry?.kind || "none"}
      className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-neutral-100">
          <Receipt size={18} weight="duotone" className="text-primary-hover" />
        </span>
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
            {invoice.title}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            Issued {new Date(invoice.issue_date).toLocaleDateString()}
            {invoice.due_date ? ` · Due ${new Date(invoice.due_date).toLocaleDateString()}` : ""}
            {invoice.paid_at ? ` · Paid ${new Date(invoice.paid_at).toLocaleDateString()}` : ""}
          </p>
          {retry && (
            <p
              data-testid={`parent-invoice-${invoice.id}-retry`}
              data-retry-kind={retry.kind}
              className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                retry.kind === "needs_action"
                  ? "bg-warning-soft text-warning"
                  : "bg-neutral-100 text-neutral-600"
              }`}
            >
              <Lightning size={10} weight="duotone" />
              {retry.label} · attempt {retry.attempts} of 3
            </p>
          )}
          <ul className="mt-2 space-y-0.5 text-xs text-neutral-700">
            {(invoice.line_items || []).map((li) => (
              <li key={li.id} className="flex justify-between gap-3">
                <span className="truncate">{li.description}</span>
                <span className="flex-shrink-0 font-mono">
                  {li.quantity > 1 ? `${li.quantity} × ` : ""}
                  {(li.unit_amount_cents / 100).toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
          {hasAgencyCoverage && (
            <div
              data-testid={`parent-invoice-${invoice.id}-agency-coverage`}
              className="mt-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-xs leading-relaxed text-neutral-700"
            >
              <p className="font-semibold text-neutral-900">Agency coverage is being reconciled</p>
              <p className="mt-0.5">Your center is reconciling the covered portion. Online checkout and autopay are paused for this invoice; any family portion is recorded separately by your center.</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-3 sm:flex-col sm:items-end">
        <div className="text-right">
          <p className="overline text-neutral-500">
            {hasAgencyCoverage ? "Your responsibility" : invoiceStatusLabel(invoice.status)}
          </p>
          <p className="tnum mt-1 font-heading text-lg font-bold text-neutral-900">
            {((hasAgencyCoverage ? familyOpen : invoice.amount_total_cents) / 100).toFixed(2)} {(invoice.currency || "usd").toUpperCase()}
          </p>
          {hasAgencyCoverage && (
            <p className="mt-1 text-[11px] text-neutral-500">
              Invoice total {(invoice.amount_total_cents / 100).toFixed(2)} {(invoice.currency || "usd").toUpperCase()}
            </p>
          )}
        </div>
        {unpaid && !hasAgencyCoverage && (
          <button
            type="button"
            data-testid={`parent-pay-invoice-${invoice.id}`}
            onClick={onPay}
            disabled={paying}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:opacity-60"
          >
            {paying ? "Opening checkout…" : "Pay now"}
            {!paying && <CaretRight size={12} weight="bold" />}
          </button>
        )}
        {unpaid && !hasAgencyCoverage && showAutopayNudge && (
          <button
            type="button"
            data-testid={`parent-invoice-${invoice.id}-autopay-nudge`}
            onClick={onSetupAutopay}
            disabled={autopayBusy}
            className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary-hover transition-all hover:bg-primary/10 disabled:opacity-60"
          >
            <Lightning size={11} weight="duotone" />
            Autopay this next time
          </button>
        )}
        <button
          type="button"
          data-testid={`parent-invoice-${invoice.id}-pdf`}
          onClick={() => {
            openParentInvoicePdf(invoice.id).catch((err) => {
              toast.error(err.message || "Could not open PDF");
            });
          }}
          className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600 hover:bg-neutral-50"
          title="Download invoice PDF"
        >
          <Receipt size={11} weight="duotone" />
          PDF
        </button>
      </div>
    </div>
  );
}
function AutopayCard({ profile, busy, onSetup, onDisable, openInvoices = [], coveredInvoiceCount = 0 }) {
  const status = profile?.autopay_status || "none";
  const isActive = status === "active";
  const isPending = status === "pending_setup";
  const isFailure = status === "setup_failed" || status === "payment_failed";

  // Pick the most relevant retry from open invoices: prefer needs_action,
  // else scheduled, else nothing.
  const retryHint = (() => {
    if (status !== "payment_failed") return null;
    const hints = openInvoices
      .map((inv) => describeAutopayRetry(inv))
      .filter(Boolean);
    return (
      hints.find((h) => h.kind === "needs_action") ||
      hints.find((h) => h.kind === "scheduled") ||
      null
    );
  })();

  return (
    <section
      data-testid="parent-autopay-card"
      data-autopay-status={status}
      className={`rounded-2xl border p-5 shadow-card ${
        isActive
          ? "border-primary/30 bg-primary/5"
          : isFailure
            ? "border-danger/40 bg-danger-soft"
            : "border-neutral-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
            isActive ? "bg-primary/10" : isFailure ? "bg-danger/10" : "bg-neutral-100"
          }`}
        >
          {isActive ? (
            <ShieldCheck size={20} weight="duotone" className="text-primary-hover" />
          ) : isFailure ? (
            <WarningCircle size={20} weight="duotone" className="text-danger" />
          ) : (
            <Lightning size={20} weight="duotone" className="text-neutral-500" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
              Automatic payments
            </p>
            <AutopayStatusChip status={status} testId="parent-autopay-status-chip" />
          </div>
          <p
            className="mt-1 text-xs leading-relaxed text-neutral-600"
            data-testid="parent-autopay-description"
          >
            {isActive
              ? "We'll charge your saved card whenever a new tuition invoice is issued."
              : isPending
                ? "We're finishing the setup with Stripe — this only takes a moment."
                : status === "setup_failed"
                  ? "The last setup attempt didn't complete. You can try again below."
                  : status === "payment_failed"
                    ? "Your most recent autopay charge failed. Please re-enroll a card."
                    : "Save a card once and we'll pay each tuition invoice automatically on its due date."}
          </p>
          {profile?.autopay_failure_reason ? (
            <p
              data-testid="parent-autopay-failure-reason"
              className="mt-2 text-[11px] text-danger"
            >
              {profile.autopay_failure_reason}
            </p>
          ) : null}
          {retryHint ? (
            <p
              data-testid="parent-autopay-next-retry"
              data-retry-kind={retryHint.kind}
              className="mt-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-[11px] leading-relaxed text-neutral-700"
            >
              <span className="font-semibold">{retryHint.label}.</span>{" "}
              {retryHint.detail}
            </p>
          ) : null}
          {coveredInvoiceCount > 0 ? (
            <p
              data-testid="parent-autopay-agency-coverage-note"
              className="mt-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-[11px] leading-relaxed text-neutral-700"
            >
              {coveredInvoiceCount === 1 ? "One agency-covered invoice is" : `${coveredInvoiceCount} agency-covered invoices are`} excluded from autopay while your center reconciles coverage.
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {isActive ? (
          <>
            <button
              type="button"
              data-testid="parent-autopay-update-card"
              disabled={busy}
              onClick={onSetup}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
            >
              {busy ? "Opening checkout…" : "Update card"}
            </button>
            <button
              type="button"
              data-testid="parent-autopay-disable"
              disabled={busy}
              onClick={onDisable}
              className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-white px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger-soft disabled:opacity-60"
            >
              {busy ? "Working…" : "Turn off autopay"}
            </button>
          </>
        ) : isPending ? (
          <button
            type="button"
            data-testid="parent-autopay-retry"
            disabled={busy}
            onClick={onSetup}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
          >
            {busy ? "Opening checkout…" : "Start over"}
          </button>
        ) : (
          <button
            type="button"
            data-testid="parent-autopay-setup"
            disabled={busy}
            onClick={onSetup}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:opacity-60"
          >
            {busy
              ? "Opening checkout…"
              : status === "setup_failed"
                ? "Try again"
                : "Set up autopay"}
            {!busy && <CaretRight size={12} weight="bold" />}
          </button>
        )}
      </div>
    </section>
  );
}
